import { computed, observable, makeObservable } from 'mobx';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { Common } from 'src/domains/common/Common';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateExpectNumber, validateRequire } from '../../ValidatorsNew';
import { SelectedOptionType } from '../../form/selectNew/SelectNew';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { Amount } from 'src_common/common/amount/Amount';

export type OddsTypeValuesType = 'fractional' | 'decimal';

export class SettingsFormState {
    @observable public savingOddsType: boolean = false;
    private readonly languagesState: LanguagesState;
    private readonly usersState: UsersState;
    private readonly basicDataModel: BasicDataModel;
    private localStorageState: LocalStorageState;
    public oddsFormatInput: FormInputState<string, number>;

    public constructor(private readonly common: Common) {
        makeObservable(this);
        this.languagesState = LanguagesState.get(common);
        this.usersState = UsersState.get(common);
        this.basicDataModel = BasicDataModel.get(common);
        this.localStorageState = LocalStorageState.get(this.common);
        const defaultValue = this.basicDataModel.oddsFormatShort === 'f' ? 'fractional' : 'decimal';
        this.oddsFormatInput = FormInputState.new(defaultValue).map(validateRequire).map(validateExpectNumber);
    }

    @computed public get oddsFormatOptions(): SelectedOptionType[] {
        return [
            {
                label: this.languagesState.getTranslation(
                    'account.preferences-tab.odds-format.fractional',
                    'Fractional'
                ),
                value: 'fractional',
            },
            {
                label: this.languagesState.getTranslation('account.preferences-tab.odds-format.decimal', 'Decimal'),
                value: 'decimal',
            },
        ];
    }

    @computed public get balance() {
        const playableBalance = this.usersState.walletData.valueReady?.playableBalance;

        if (playableBalance === undefined) {
            return null;
        }
        return this.basicDataModel.money(new Amount(playableBalance));
    }

    @computed public get showBalanceInHeader(): boolean {
        return this.localStorageState.balanceInHeader.getValue();
    }

    private onChangeOddsType = async (oddsFormat: OddsTypeValuesType): Promise<void> => {
        try {
            await this.common.trpcClient.client.accounts.changeOddsFormat.mutate({ oddsFormat });
        } catch (error) {
            console.error('changeOddsFormat - Incorrect response code', error);
        }
    };

    public updateOddsTypeIfChanged = async (newValue: string | undefined): Promise<void> => {
        if (this.savingOddsType) {
            return;
        }

        if (newValue !== 'fractional' && newValue !== 'decimal') {
            return;
        }

        this.savingOddsType = true;

        try {
            const usersState = UsersState.get(this.common);

            await this.onChangeOddsType(newValue);
            await usersState.basicData.refreshAndWait();
        } catch (error) {
            console.error(error);
        }

        this.savingOddsType = false;
    };

    public updateBalanceInHeaderIfChanged = (): void => {
        this.localStorageState.balanceInHeader.setValue(!this.showBalanceInHeader);
    };
}
